import { Injectable } from '@angular/core';
import { filter, map, Observable, tap } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as dynamicFormsActions from './dynamic-forms.actions';
import * as dynamicFormsSelectors from './dynamic-forms.selectors';
import * as dynamicFormsReducer from './dynamic-forms.reducer';
import { FormDefinition } from '@wdx/shared/utils';
import {
    FormFrameworkEvent,
    FormFrameworkEventAction,
    FormFrameworkEventType,
} from '../../models';
import * as R from 'ramda';

@Injectable()
export class DynamicFormsFacade {
    constructor(
        private store$: Store<{
            [dynamicFormsReducer.FEATURE_KEY]: dynamicFormsReducer.State;
        }>,
    ) {}

    private getLatestEvents$(
        timeStamp: number = Date.now(),
    ): Observable<FormFrameworkEvent> {
        return this.store$.select(dynamicFormsSelectors.getLatestEvent).pipe(
            filter(
                (event) =>
                    Boolean(event?.event) &&
                    Boolean(event?.timeStamp && event.timeStamp > timeStamp),
            ),
            map((event) => event?.event as FormFrameworkEvent),
        );
    }

    public getFormFrameworkEvents$(filters: {
        types?: FormFrameworkEventType[];
        formNames?: string[];
    }) {
        return this.getLatestEvents$().pipe(
            filter(
                (event) =>
                    !filters.types?.length ||
                    filters.types?.includes(
                        event.type as FormFrameworkEventType,
                    ),
            ),
            filter(
                (event) =>
                    !filters.formNames?.length ||
                    filters.formNames.includes(
                        event?.action?.formName as string,
                    ),
            ),
        );
    }

    public getFormSaveEvents$(formNames?: string[]) {
        return this.getFormFrameworkEvents$({
            types: [
                FormFrameworkEventType.SaveSuccess,
                FormFrameworkEventType.SaveStart,
                FormFrameworkEventType.SaveFailed,
            ],
            formNames,
        });
    }

    public newFormFrameworkEvent(event: FormFrameworkEvent) {
        const action = R.clone(
            R.omit(
                ['layoutAndDefinition$'],
                event.action as FormFrameworkEventAction,
            ),
        );
        return this.store$.dispatch(
            dynamicFormsActions.newFormEvent({
                event: { event: { ...event, action }, timeStamp: Date.now() },
            }),
        );
    }

    getFormDefinition$(formId: string): Observable<FormDefinition> {
        return this.store$.pipe(
            select(dynamicFormsSelectors.getFormDefinitionCrudObject, {
                id: formId,
            }),
            tap((crudObj) => {
                if (
                    !crudObj?.single &&
                    !crudObj?.isLoadingSingle &&
                    !crudObj?.hasLoadingSingleError
                ) {
                    this.store$.dispatch(
                        dynamicFormsActions.getFormDefinition({
                            formId,
                        }),
                    );
                }
            }),
            map((crudObj) => crudObj?.single),
            filter(Boolean),
        );
    }

    getIsUpdatingFormData$(formId: string): Observable<boolean> {
        return this.store$.pipe(
            select(dynamicFormsSelectors.getIsUpdatingFormData, { id: formId }),
            map(Boolean),
        );
    }
}
